import useOnScreen from '@rio-cloud/rio-uikit/hooks/useOnScreen';
import { useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import LabeledValue from '#components/shared/LabeledValue.tsx';
import { TourStopList } from '#components/tourdetails/TourStopList.tsx';
import { TourStopProgress } from '#components/tourdetails/TourStopProgress.tsx';
import { TourStopProgressHeader } from '#components/tourdetails/TourStopProgressHeader.tsx';
import { TRACKING_ACTIONS, TRACKING_CATEGORIES } from '#configuration/setup/googleAnalytics.ts';
import type { Driver } from '#domain/model/Driver.ts';
import type { Tour } from '#domain/model/Tour.ts';
import { useTrackScreenSeen } from '#hooks/useTrackScreenSeen.ts';

type WidgetTourDetailsSidebarProps = {
    title: string;
    href?: string;
    tour: Tour;
    driver?: Driver;
    setSelectedStopId: (stopId?: string) => void;
};

const formatMetaValue = (value?: string) => value?.trim();

export const WidgetTourDetails = ({ title, href, tour, driver, setSelectedStopId }: WidgetTourDetailsSidebarProps) => {
    const intl = useIntl();
    const ref = useRef(null);
    const onScreen = useOnScreen(ref);

    useTrackScreenSeen(onScreen, {
        category: TRACKING_CATEGORIES.LIVEMONITOR,
        action: TRACKING_ACTIONS.ON_SCREEN,
        label: 'Show tour details view',
    });

    const hasMetaValues = tour.trailer.licensePlate || tour.note || driver?.name;

    return (
        <div data-testid='tour-details' ref={ref}>
            <div className='bg-white list-group border rounded margin-bottom-20 shadow-subtle'>
                <div className='display-flex flex-column gap-15 padding-15'>
                    <div>
                        <TourStopProgressHeader tour={tour} title={title} />
                        {tour.status !== 'planned' && <TourStopProgress stops={tour.stops} />}
                    </div>
                    {hasMetaValues && (
                        <div className='display-grid grid-auto-fit-150 gap-15'>
                            {driver?.name && (
                                <LabeledValue
                                    label={intl.formatMessage({
                                        id: 'intl-msg:order-communication-web.livemonitor.driver',
                                    })}
                                    value={<span className='line-height-125rel'>{formatMetaValue(driver?.name)}</span>}
                                    icon='driver'
                                    classNameValue='text-medium margin-top-3'
                                />
                            )}
                            {tour.trailer.licensePlate && (
                                <LabeledValue
                                    label={intl.formatMessage({
                                        id: 'intl-msg:order-communication-web.livemonitor.trailer',
                                    })}
                                    value={
                                        <span className='line-height-125rel'>
                                            {formatMetaValue(tour.trailer.licensePlate)}
                                        </span>
                                    }
                                    icon='trailer'
                                    classNameValue='text-medium margin-top-3'
                                />
                            )}
                            {tour.note && (
                                <LabeledValue
                                    label={intl.formatMessage({
                                        id: 'intl-msg:order-communication-web.livemonitor.tournote',
                                    })}
                                    value={<span className='line-height-125rel'>{formatMetaValue(tour.note)}</span>}
                                    icon='comment'
                                    classNameValue='text-medium margin-top-3'
                                />
                            )}
                        </div>
                    )}
                </div>
                {Boolean((tour.firstStop && tour.lastStop) || href) && (
                    <div
                        className={
                            'bg-lightest border-rounded text-color-dark list-group-item padding-y-5 display-flex text-size-12 align-items-center'
                        }
                    >
                        {tour.firstStop && tour.lastStop && (
                            <div>{`${tour.firstStop.name} \u2192 ${tour.lastStop.name}`}</div>
                        )}
                        {href && (
                            <div className='flex-1-0 text-right'>
                                <a data-testid='livemonitor-tour-link' href={href} target='_top'>
                                    <FormattedMessage id='intl-msg:order-communication-web.livemonitor.extendedTourView' />
                                </a>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div>
                <TourStopList tour={tour} setSelectedStopId={setSelectedStopId} />
            </div>
        </div>
    );
};
